import { useMemo } from "react";
import useSWR from "swr";
import { fetchPrograms } from "@/lib/api";
import { getProgramCardImage, getProgramSourceUrl } from "@/data/programDetails";
import { PROGRAMS_MOCK } from "@/data/programsMock";
import { SKILL_BOOKING_BY_API_ID, SKILL_PROGRAMS } from "@/data/skillPrograms";

/**
 * Program data layer.
 *
 * UI components (ProgramCard, ProgramGrid, pages) must ONLY consume the
 * normalized `Program` shape produced here. When the backend contract changes,
 * change `normalizeProgram` — not the components.
 *
 * Normalized Program:
 * {
 *   id, slug, title, summary,
 *   image, imageAlt,
 *   industry,              // industry id, e.g. "health"
 *   mode,                  // "Virtual" | "In Person" | ...
 *   duration,              // "4 Weeks"
 *   startDate,             // free text or ISO date string
 *   venue,
 *   price: { amount, currency } | null,
 *   href, external,        // link target for the card
 *   featured,
 *   kind,                  // "Virtual internship" | "Skill development" | "In-person programme" | ...
 *   ages,                  // "13-18"
 *   source,                // "catalog" (local data) | "api" (backend)
 * }
 */

/** Include the local internship catalog (src/data/programsMock.js). Set to false once the API lists every internship. */
export const USE_MOCK_FALLBACK = true;

const SITE_ORIGIN = "https://alearninglab.com";
const PLACEHOLDER_IMAGE = `${SITE_ORIGIN}/wp-content/uploads/2025/08/Stanford-004.png`;

const GENERIC_CARD_IMAGES = /Medical-01\.webp|VI-01\.webp|Stanford-004|Law-01\.webp/i;

function isGenericCardImage(src) {
  if (!src) return true;
  return GENERIC_CARD_IMAGES.test(String(src));
}

function stripHtml(html = "") {
  return String(html).replace(/<[^>]+>/g, "").replace(/\s+/g, " ").trim();
}

function slugify(value = "") {
  return String(value)
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function resolveImage(src) {
  if (!src) return PLACEHOLDER_IMAGE;
  if (/^https?:\/\//i.test(src)) return src;
  return `${SITE_ORIGIN}${src.startsWith("/") ? "" : "/"}${src}`;
}

/** Stable slugs for API rows that do not send `slug` (matches programDetails / industry cards). */
const API_SLUG_BY_ID = {
  328: "ai-and-computer-science-virtual-internship",
  329: "engineering-virtual-internship",
};

function inferProgramKind(raw = {}) {
  const title = String(raw.title || raw.name || "").toLowerCase();
  const mode = String(raw.mode || "").toLowerCase();
  if (
    /workshop|portfolio builder|excel|financial statements|competitive writing|career assessment|assessment test/i.test(
      title,
    )
  ) {
    return "Skill development";
  }
  if (/bootcamp|observership/i.test(title) || mode.includes("in person")) {
    return "In-person programme";
  }
  if (/internship|shadowing/i.test(title)) {
    return "Virtual internship";
  }
  if (mode.includes("virtual") || mode.includes("online")) {
    return "Skill development";
  }
  return "In-person programme";
}

function findSkillCatalogEntry(raw) {
  const apiId = Number(raw.id);
  if (Number.isFinite(apiId) && SKILL_BOOKING_BY_API_ID[apiId]) {
    return SKILL_PROGRAMS.find((p) => p.apiId === apiId);
  }
  const slug = raw.slug || slugify(raw.title || raw.name || "");
  return SKILL_PROGRAMS.find((p) => p.slug === slug || raw.id === `skill-${p.slug}`);
}

/** Maps one raw backend program (or mock entry) to the normalized shape. Tolerates missing fields. */
export function normalizeProgram(raw = {}) {
  const catalogSkill = findSkillCatalogEntry(raw);
  const title = raw.title || raw.name || "Untitled programme";
  const apiId = Number(raw.id);
  let slug = raw.slug || slugify(title);
  if (Number.isFinite(apiId) && API_SLUG_BY_ID[apiId]) {
    slug = API_SLUG_BY_ID[apiId];
  }
  const amount = Number(raw.price);
  const kind = raw.kind || catalogSkill?.kind || inferProgramKind(raw) || "";
  const enrolUrl =
    raw.enrolUrl ||
    raw.enrol_url ||
    raw.booking_url ||
    catalogSkill?.enrolUrl ||
    (Number.isFinite(apiId) ? SKILL_BOOKING_BY_API_ID[apiId] : "") ||
    "";
  const url = raw.url || raw.href || "";
  const liveListingUrl = kind === "Virtual internship" ? getProgramSourceUrl(slug) : null;
  let href =
    kind === "Skill development" && enrolUrl
      ? enrolUrl
      : liveListingUrl || url || enrolUrl || `/consultation?program=${encodeURIComponent(title)}`;
  let external = /^https?:\/\//i.test(href);

  const rawThumb = raw.image || raw.thumbnail;
  let image = resolveImage(rawThumb);
  const detailCardImage = getProgramCardImage(slug);
  if (
    detailCardImage &&
    (!rawThumb || isGenericCardImage(rawThumb) || image === PLACEHOLDER_IMAGE)
  ) {
    image = detailCardImage;
  }

  return {
    id: String(raw.id ?? slug),
    slug,
    title,
    summary: stripHtml(raw.summary || raw.description || ""),
    image,
    imageAlt: raw.image_alt || raw.imageAlt || title,
    industry: raw.industry || raw.subject || "",
    mode: raw.mode || "",
    duration: raw.duration || "",
    startDate: raw.start_date || raw.startDate || raw.date || "",
    venue: raw.venue || "",
    price: Number.isFinite(amount) && amount > 0 ? { amount, currency: raw.currency || "INR" } : null,
    href,
    external,
    featured: Boolean(raw.featured),
    kind,
    ages: raw.ages || "",
    source: raw.source || "api",
  };
}

export function formatPrice(price) {
  if (!price) return "";
  const locale = price.currency === "INR" ? "en-IN" : "en-US";
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: price.currency,
    maximumFractionDigits: 0,
  }).format(price.amount);
}

function matchesIndustry(program, industryId) {
  return slugify(program.industry) === slugify(industryId);
}

/**
 * Backend ids of programmes that the local catalog already covers (virtual internships and workshops),
 * so they are not listed twice when the API returns them as well.
 */
const CATALOG_API_IDS = new Set([
  324, 325, 326, 327, 328, 329, 330, 331, 332, 333, 334, 335, 336, 337, 338, // virtual internships
  339, 340, 341, 342, // skill workshops (local catalog)
]);

/**
 * The always-available catalog: the 13 writer-listed virtual internships plus skill development programmes.
 * It renders immediately (no network wait) so cards never disappear when the API is slow or down.
 * Catalog entries are marked `source: "catalog"` and must not be emitted as structured data.
 */
const CATALOG = [
  ...(USE_MOCK_FALLBACK
    ? PROGRAMS_MOCK.map((p) => ({ ...p, kind: "Virtual internship", ages: "13-18", source: "catalog" }))
    : []),
  ...SKILL_PROGRAMS.map((p) => ({ ...p, source: "catalog" })),
].map(normalizeProgram);

function apiPrograms(data) {
  return (data ?? [])
    .filter((raw) => !USE_MOCK_FALLBACK || !CATALOG_API_IDS.has(Number(raw.id)))
    .map((raw) => normalizeProgram(raw));
}

/**
 * Programs for one industry page: catalog programmes for that industry plus any matching backend programmes.
 * `isFallback` is true when nothing came from the backend, so callers can skip structured data.
 */
export function useIndustryPrograms(industryId) {
  const { data, error, isLoading, mutate } = useSWR("india-programs", fetchPrograms);

  return useMemo(() => {
    const fromApi = apiPrograms(data).filter((p) => matchesIndustry(p, industryId));
    const fromCatalog = CATALOG.filter((p) => matchesIndustry(p, industryId));
    const programs = [...fromCatalog, ...fromApi];

    return {
      programs,
      isLoading: isLoading && programs.length === 0,
      error: programs.length === 0 ? error ?? null : null,
      isFallback: fromApi.length === 0,
      retry: mutate,
    };
  }, [data, error, isLoading, industryId, mutate]);
}

/** Every programme for the /programs page: the catalog plus backend programmes the catalog does not cover. */
export function useAllPrograms() {
  const { data, error, isLoading, mutate } = useSWR("india-programs", fetchPrograms);

  return useMemo(
    () => ({
      programs: [...CATALOG, ...apiPrograms(data)],
      isLoading: false,
      error: null,
      apiError: error ?? null,
      apiLoading: isLoading,
      retry: mutate,
    }),
    [data, error, isLoading, mutate],
  );
}
